import { Button, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Loader from 'src/components/common/Loader';
import { PopupComponent } from 'src/components/common/PopupComponent';
import {
  Alert,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import moment from 'moment';
import { CRFDetails, SubjectVisit } from 'src/types/SubjectCalenderVisits';

interface ScheduleEventDialogProps {
  isDialogOpen: boolean;
  selectedSubjectVisit: SubjectVisit;
  onClose(): void;
}

export default function ShowSubjectVisitDialog(props: ScheduleEventDialogProps) {
  const { t } = useTranslation();
  const { userPreferredDateTimeFormat, userPreferedDateFormat } = usePreferedDateFormat();

  return (
    <PopupComponent open={props.isDialogOpen}>
      <Loader />
      <DialogTitle>
        {props?.selectedSubjectVisit?.unscheduled_visit_name
          ? props?.selectedSubjectVisit?.unscheduled_visit_name
          : props.selectedSubjectVisit.protocol_visit_name}
        :{' '}
        <span style={{ color: 'rgba(0,0,0,0.6)' }}>
          {moment(`${props.selectedSubjectVisit.start_on}Z`).format('MMMM Do, YYYY')}
        </span>
      </DialogTitle>
      <DialogContent sx={{ maxWidth: '620px', minWidth: '600px' }}>
        <Grid container spacing={2} mb="2" pt={1}>
          <Grid item md={6}>
            <FormControl
              fullWidth
              size="medium"
              sx={{
                '& .MuiInputLabel-root[data-shrink="false"]': { transform: 'translate( 0, 10px) scale(1)' },
                '& .MuiInputLabel-root[data-shrink="true"]': { transform: 'translate( 0, -9px) scale(0.75)' }
              }}
              disabled
            >
              <InputLabel id="protocolVisit-select-label">Protocol Visit Name</InputLabel>
              <Select
                labelId="protocolVisit-select-label"
                id="protocolVisit-select"
                value={
                  props?.selectedSubjectVisit?.unscheduled_visit_name
                    ? props.selectedSubjectVisit.unscheduled_visit_name
                    : props.selectedSubjectVisit.protocol_visit_name
                }
                label="Protocol Visit Name"
                disabled
              >
                {props?.selectedSubjectVisit?.unscheduled_visit_name ? (
                  <MenuItem value={props.selectedSubjectVisit.unscheduled_visit_name}>
                    {props.selectedSubjectVisit.unscheduled_visit_name}
                  </MenuItem>
                ) : (
                  <MenuItem value={props.selectedSubjectVisit.protocol_visit_name}>
                    {props.selectedSubjectVisit.protocol_visit_name}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item md={6}>
            <FormControl
              fullWidth
              size="medium"
              sx={{
                '& .MuiInputLabel-root[data-shrink="false"]': { transform: 'translate( 0, 10px) scale(1)' },
                '& .MuiInputLabel-root[data-shrink="true"]': { transform: 'translate( 0, -9px) scale(0.75)' }
              }}
              disabled
            >
              <InputLabel id="protocolVisit-select-label">Visit Type</InputLabel>
              <Select
                labelId="protocolVisit-select-label"
                id="protocolVisit-select"
                value={props.selectedSubjectVisit.visit_type}
                label="Protocol Visit Name"
                disabled
              >
                <MenuItem value={props.selectedSubjectVisit.visit_type}>
                  {props.selectedSubjectVisit.visit_type}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormControl fullWidth disabled>
                <DateTimePicker
                  className="modifylabel"
                  label="Start Date and Time"
                  value={`${props.selectedSubjectVisit.start_on}Z`}
                  inputFormat={userPreferredDateTimeFormat}
                  onChange={() => undefined}
                  onAccept={() => undefined}
                  disabled
                  renderInput={(params: any) => <TextField {...params} required={false} disabled />}
                />
              </FormControl>
            </LocalizationProvider>
          </Grid>

          <Grid item md={6}>
            <FormControl
              fullWidth
              size="medium"
              sx={{
                '& .MuiInputLabel-root[data-shrink="false"]': { transform: 'translate( 0, 10px) scale(1)' },
                '& .MuiInputLabel-root[data-shrink="true"]': { transform: 'translate( 0, -9px) scale(0.75)' }
              }}
              disabled
            >
              <InputLabel id="protocolVisit-select-label">Duration</InputLabel>
              <Select
                labelId="protocolVisit-select-label"
                id="protocolVisit-select"
                value={props.selectedSubjectVisit.duration}
                label="Protocol Visit Name"
                disabled
              >
                <MenuItem value={props.selectedSubjectVisit.duration}>
                  {props.selectedSubjectVisit.duration / 60} hours
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Typography my={2} variant="body1">
          <strong>Choose assessment(s) for this screening appointment:</strong>
        </Typography>
        <Alert severity="info">
          Screening Window:{' '}
          <strong>
            {' '}
            {`${moment(`${props.selectedSubjectVisit.start_on}Z`)
              .subtract(props.selectedSubjectVisit.minimum_visit_range, 'days')
              .format(userPreferedDateFormat.toUpperCase())} - ${moment(`${props.selectedSubjectVisit.start_on}Z`)
              .add(props.selectedSubjectVisit.maximum_visit_range, 'days')
              .format(userPreferedDateFormat.toUpperCase())}`}
          </strong>
        </Alert>

        {props.selectedSubjectVisit.crfs.length > 0 ? (
          <Grid
            container
            spacing={2}
            mt={0.25}
            sx={{ '& .MuiFormGroup-root .MuiFormControlLabel-root': { marginBottom: '8px' } }}
          >
            {props.selectedSubjectVisit.crfs.map((eachCRF: CRFDetails) => (
              <Grid key={eachCRF.crf_OID} item md={4}>
                <FormControlLabel
                  control={<Checkbox defaultChecked disabled />}
                  label={eachCRF?.custom_name ? eachCRF?.custom_name : eachCRF.crf_OID}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <></>
        )}
      </DialogContent>
      <DialogActions>
        <br />
        <Button onClick={props.onClose} className="grey-btn">
          {t('Close')}
        </Button>
      </DialogActions>
    </PopupComponent>
  );
}
